import React, { useState, useEffect } from 'react';
import { Download, Wifi, WifiOff, RefreshCw, X, Bell, Smartphone } from 'lucide-react';
import { usePWAInstall, useServiceWorker, useNetworkStatus, usePushNotifications, isRunningAsPWA } from '../hooks/usePWA';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// PWA Install Banner
export const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const { theme } = useTheme();
  const [dismissed, setDismissed] = useState(false);

  // Check if banner was previously dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      toast.success('Application installée avec succès !');
    }
  };

  if (!isInstallable || isInstalled || dismissed) {
    return null;
  }

  return (
    <div className={cn(
      'fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50',
      'rounded-xl p-4 shadow-2xl border backdrop-blur-xl',
      'animate-slide-up',
      theme === 'dark' 
        ? 'bg-zinc-900/95 border-white/10' 
        : 'bg-white/95 border-gray-200'
    )} data-testid="pwa-install-banner">
      <button
        onClick={handleDismiss}
        className={cn(
          'absolute top-2 right-2 p-1 rounded-lg transition-colors',
          theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
        )}
        aria-label="Fermer"
      >
        <X size={16} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
      </button>
      
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
          'bg-gradient-to-br from-red-500 to-red-600'
        )}>
          <Smartphone size={24} className="text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={cn(
            'font-semibold text-sm mb-1',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            Installer BYTRANSPORT
          </h3>
          <p className={cn(
            'text-xs mb-3',
            theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
          )}>
            Ajoutez l'app sur votre écran d'accueil pour un accès rapide et hors ligne
          </p>
          
          <button
            onClick={handleInstall}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="pwa-install-button"
          >
            <Download size={16} />
            Installer
          </button>
        </div>
      </div>
    </div>
  );
};

// Service Worker Update Banner
export const UpdateBanner = () => {
  const { hasUpdate, updateServiceWorker } = useServiceWorker();
  const { theme } = useTheme();

  if (!hasUpdate) {
    return null;
  }

  return (
    <div className={cn(
      'fixed top-20 md:top-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50',
      'rounded-xl p-4 shadow-2xl border',
      theme === 'dark' 
        ? 'bg-cyan-900/95 border-cyan-500/30' 
        : 'bg-cyan-50 border-cyan-200'
    )} data-testid="update-banner">
      <div className="flex items-center gap-3">
        <RefreshCw size={20} className={theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'} />
        <div className="flex-1">
          <p className={cn(
            'text-sm font-medium',
            theme === 'dark' ? 'text-cyan-100' : 'text-cyan-800'
          )}>
            Mise à jour disponible
          </p>
        </div>
        <button
          onClick={updateServiceWorker}
          className="px-3 py-1 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-xs font-medium transition-colors"
        >
          Actualiser
        </button>
      </div>
    </div>
  );
};

// Offline Status Banner
export const OfflineBanner = () => {
  const { isOnline } = useNetworkStatus();
  const { theme } = useTheme();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShow(true);
    } else {
      // Delay hiding to show "back online" message
      const timer = setTimeout(() => setShow(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  if (!show && isOnline) {
    return null;
  }

  return (
    <div className={cn(
      'fixed top-0 left-0 right-0 z-50 px-4 py-2',
      'flex items-center justify-center gap-2 text-sm font-medium',
      'transition-all duration-300',
      isOnline 
        ? 'bg-green-500 text-white' 
        : theme === 'dark' 
          ? 'bg-amber-600 text-amber-100' 
          : 'bg-amber-500 text-white'
    )} data-testid="offline-banner">
      {isOnline ? (
        <>
          <Wifi size={16} />
          <span>Connexion rétablie</span>
        </>
      ) : (
        <>
          <WifiOff size={16} />
          <span>Vous êtes hors ligne - Les données peuvent être obsolètes</span>
        </>
      )}
    </div>
  );
};

// Notification Permission Request
export const NotificationPrompt = ({ onClose }) => {
  const { permission, requestPermission } = usePushNotifications();
  const { theme } = useTheme();

  if (permission === 'granted' || permission === 'denied') {
    return null;
  }

  const handleRequest = async () => {
    const result = await requestPermission();
    if (result === 'granted') {
      toast.success('Notifications activées !');
    }
    onClose?.();
  };

  return (
    <div className={cn(
      'p-4 rounded-xl border',
      theme === 'dark' 
        ? 'bg-zinc-800/50 border-white/10' 
        : 'bg-gray-50 border-gray-200'
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center',
          'bg-amber-500/20'
        )}>
          <Bell size={20} className="text-amber-500" />
        </div>
        
        <div className="flex-1">
          <h4 className={cn(
            'font-medium text-sm mb-1',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            Activer les notifications
          </h4>
          <p className={cn(
            'text-xs mb-3',
            theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
          )}>
            Recevez des alertes pour les incidents, tâches et messages importants
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleRequest}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-colors"
            >
              Activer
            </button>
            <button
              onClick={onClose}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                theme === 'dark' 
                  ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              )}
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// PWA Status Info (for Settings page)
export const PWAStatusCard = () => {
  const { isInstalled } = usePWAInstall();
  const { isReady } = useServiceWorker();
  const { isOnline } = useNetworkStatus();
  const { permission } = usePushNotifications();
  const { theme } = useTheme();
  const isPWA = isRunningAsPWA();

  const statusItems = [
    {
      label: 'Mode PWA',
      value: isPWA ? 'Activé' : 'Navigateur',
      status: isPWA ? 'success' : 'info'
    },
    {
      label: 'Installation',
      value: isInstalled ? 'Installée' : 'Non installée',
      status: isInstalled ? 'success' : 'warning'
    },
    {
      label: 'Service Worker',
      value: isReady ? 'Actif' : 'Inactif',
      status: isReady ? 'success' : 'error'
    },
    {
      label: 'Connexion',
      value: isOnline ? 'En ligne' : 'Hors ligne',
      status: isOnline ? 'success' : 'warning'
    },
    {
      label: 'Notifications',
      value: permission === 'granted' ? 'Activées' : permission === 'denied' ? 'Bloquées' : 'Non configurées',
      status: permission === 'granted' ? 'success' : permission === 'denied' ? 'error' : 'warning'
    }
  ];

  const statusColors = {
    success: theme === 'dark' ? 'text-green-400' : 'text-green-600',
    warning: theme === 'dark' ? 'text-amber-400' : 'text-amber-600',
    error: theme === 'dark' ? 'text-red-400' : 'text-red-600',
    info: theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'
  };

  return (
    <div className="glass-card p-5" data-testid="pwa-status-card">
      <h3 className={cn(
        'font-semibold mb-4',
        theme === 'dark' ? 'text-white' : 'text-gray-900'
      )}>
        Statut Application
      </h3>
      
      <div className="space-y-3">
        {statusItems.map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className={cn(
              'text-sm',
              theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
            )}>
              {item.label}
            </span>
            <span className={cn('text-sm font-medium', statusColors[item.status])}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default {
  PWAInstallBanner,
  UpdateBanner,
  OfflineBanner,
  NotificationPrompt,
  PWAStatusCard
};
